/**
 * @fileoverview Checkout Form Field Components
 *
 * This file contains specialized form field components for the checkout form,
 * each designed for specific data types and interaction patterns.
 *
 * Field Components:
 * - ReadOnlyField: Display-only fields for user information
 * - TextInputField: Standard text input with validation
 * - SelectField: Dropdown selection with dynamic options
 * - CurrencyDisplay: Formatted currency display with locale support
 *
 * Key Features:
 * - Consistent error handling and display across all field types
 * - Dynamic data source resolution for select fields
 * - Required field indicators with validation integration
 * - Accessible form labels and input associations
 * - Currency formatting with internationalization support
 *
 * Common Props:
 * - Field metadata with type and validation rules
 * - Current value and change handler integration
 * - Error state display with validation feedback
 * - CSS class customization for layout flexibility
 */

import React, { useEffect } from "react";
import { CheckoutFieldMetadata, CheckoutDetails } from "./CheckoutForm.types";

/**
 * Common props interface for all field components
 */
interface FieldProps {
  /** Field metadata including type, validation rules, and display properties */
  field: CheckoutFieldMetadata;
  /** Current value of the field */
  value: any;
  /** Callback function to handle value changes */
  onChange: (fieldName: string, value: any) => void;
  /** Checkout data containing dropdown options and default values */
  data: CheckoutDetails;
  /** Optional CSS class name for styling customization */
  className?: string;
  /** Optional error message to display */
  error?: string;
  /** Optional disabled state for the field */
  disabled?: boolean;
}

/**
 * ReadOnlyField Component
 *
 * Renders a non-editable field for displaying read-only information
 * such as user details that cannot be modified during checkout.
 *
 * @param props - Field props including metadata, value, and styling
 * @returns JSX element for read-only field display
 */
export const ReadOnlyField: React.FC<FieldProps> = ({ field, value, className, error }) => (
  <div className={`FormField ReadOnly ${className || ""} ${error ? "error" : ""}`}>
    <label>{field.label}</label>
    <div className="ReadOnlyValue">{value || "—"}</div>
    {error && <div className="FieldError">{error}</div>}
  </div>
);

/**
 * TextInputField Component
 *
 * Renders a standard text input field with validation support.
 * Includes required field indicators and error state display.
 *
 * @param props - Field props including metadata, value, change handler, and styling
 * @returns JSX element for text input field
 */
export const TextInputField: React.FC<FieldProps> = ({
  field,
  value,
  onChange,
  className,
  error,
}) => (
  <div className={`FormField ${className || ""} ${error ? "error" : ""}`}>
    <label htmlFor={field.name}>
      {field.label}
      {field.bRequired && " *"}
    </label>
    <input
      type="text"
      id={field.name}
      value={value || ""}
      onChange={(e) => onChange(field.name, e.target.value)}
      required={field.bRequired}
      className={error ? "error" : ""}
    />
    {error && <div className="FieldError">{error}</div>}
  </div>
);

/**
 * SelectField Component
 *
 * Renders a dropdown selection field with dynamic options loaded from the API.
 * Supports different data sources like countries and US states.
 *
 * @param props - Field props including metadata, value, change handler, data, and styling
 * @returns JSX element for select dropdown field
 */
export const SelectField: React.FC<FieldProps> = ({
  field,
  value,
  onChange,
  data,
  className,
  error,
  disabled,
}) => {
  // Get options from data source
  /**
   * Retrieves dropdown options based on the field's data source configuration
   * @returns Array of dropdown options with label and value properties
   */
  const getOptions = () => {
    if (!field.dataSource) return [];

    switch (field.dataSource) {
      case "ListOfUSStates":
        return data.listOfUSStates || [];
      case "ListOfCountries":
        return data.listOfCountries || [];
      case "ListOfSites":
        // Convert sites to standard dropdown option format with enhanced display
        return (data.listOfSites || []).map((site) => {
          const addressParts = [];
          if (site.addressLine1) addressParts.push(site.addressLine1);
          if (site.city) addressParts.push(site.city);
          if (site.stateProvince) addressParts.push(site.stateProvince);
          if (site.postalZipCode) addressParts.push(site.postalZipCode);

          const displayLabel =
            addressParts.length > 0
              ? `${site.sitesName} - ${addressParts.join(", ")}`
              : site.sitesName;

          return {
            label: displayLabel,
            value: site.sitesId,
          };
        });
      default:
        return [];
    }
  };

  const options = getOptions();

  return (
    <div className={`FormField ${className || ""} ${error ? "error" : ""}`}>
      <label htmlFor={field.name}>
        {field.label}
        {field.bRequired && " *"}
      </label>
      <select
        id={field.name}
        value={value || ""}
        onChange={(e) => onChange(field.name, e.target.value)}
        required={field.bRequired}
        disabled={disabled}
        className={error ? "error" : ""}
      >
        <option value="">Select {field.label}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && <div className="FieldError">{error}</div>}
    </div>
  );
};

/**
 * SiteSelectField Component
 *
 * Renders a specialized dropdown for site selection that auto-populates
 * address fields when a site is selected. Used when autoSelectSite is enabled.
 *
 * @param props - Field props including metadata, value, change handler, data, and styling
 * @returns JSX element for site selection dropdown
 */
export const SiteSelectField: React.FC<FieldProps> = ({
  field,
  value,
  onChange,
  data,
  className,
  error,
}) => {
  const sites = data.listOfSites || [];

  // Auto-select the site if there's only one option and no value is currently selected
  useEffect(() => {
    if (sites.length === 1 && !value) {
      onChange(field.name, sites[0].sitesId);
    }
  }, [sites, value, onChange, field.name]);

  /**
   * Formats site information for display in dropdown
   * @param site - Site object containing address information
   * @returns Formatted string like "Site name - Address Line 1, City, State, Zipcode"
   */
  const formatSiteDisplay = (site: any) => {
    const parts = [site.sitesName];

    // Add address components if they exist
    const addressParts = [];
    if (site.addressLine1) addressParts.push(site.addressLine1);
    if (site.city) addressParts.push(site.city);
    if (site.stateProvince) addressParts.push(site.stateProvince);
    if (site.postalZipCode) addressParts.push(site.postalZipCode);

    if (addressParts.length > 0) {
      parts.push(addressParts.join(", "));
    }

    return parts.join(" - ");
  };

  return (
    <div className={`FormField SiteSelect ${className || ""} ${error ? "error" : ""}`}>
      <label htmlFor={field.name}>
        {field.label}
        {field.bRequired && " *"}
      </label>
      <select
        id={field.name}
        value={value || ""}
        onChange={(e) => onChange(field.name, e.target.value)}
        required={field.bRequired}
        className={error ? "error" : ""}
      >
        <option value="">Select delivery site</option>
        {sites.map((site) => (
          <option key={site.sitesId} value={site.sitesId}>
            {formatSiteDisplay(site)}
          </option>
        ))}
      </select>
      {error && <div className="FieldError">{error}</div>}
    </div>
  );
};

/**
 * CurrencyDisplay Component
 *
 * Renders a formatted currency value with proper locale formatting.
 * Uses Intl.NumberFormat for consistent currency display across browsers.
 *
 * @param props - Field props including metadata, value, and styling
 * @returns JSX element for currency display
 */
export const CurrencyDisplay: React.FC<FieldProps> = ({ field, value, className, error }) => {
  /**
   * Formats a numeric amount as currency using GB locale and GBP currency
   * @param amount - The numeric amount to format
   * @returns Formatted currency string
   */
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  return (
    <div className={`FormField Currency ${className || ""} ${error ? "error" : ""}`}>
      <label>{field.label}</label>
      <div className="CurrencyValue">{value ? formatCurrency(value) : "—"}</div>
      {error && <div className="FieldError">{error}</div>}
    </div>
  );
};
